import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  InsertDriveFile,
  Description,
  AttachFile,
} from '@mui/icons-material';

interface SequenceFile {
  id: string;
  originalFilename: string;
  fileType: 'context' | 'attachment' | 'both';
  mimeType: string;
  fileSize: number;
  variableName: string;
  description: string;
  hasTextContent: boolean;
  fileUrl: string;
  createdAt: string;
}

interface FileUploaderProps {
  sequenceId: string;
  files: SequenceFile[];
  onFileUploaded: (file: SequenceFile) => void;
  onFileDeleted: (fileId: string) => void;
  fileTypeFilter?: 'context' | 'attachment' | 'both';
  title?: string;
  description?: string;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileIcon = (mimeType: string) => {
  if (mimeType?.startsWith('text/')) return <Description />;
  if (mimeType === 'application/pdf') return <Description />;
  if (mimeType?.includes('word')) return <Description />;
  return <InsertDriveFile />;
};

export const FileUploader: React.FC<FileUploaderProps> = ({
  sequenceId,
  files,
  onFileUploaded,
  onFileDeleted,
  fileTypeFilter,
  title = 'File Uploads',
  description = 'Upload files to use in your sequence',
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileType, setFileType] = useState<'context' | 'attachment' | 'both'>(fileTypeFilter || 'attachment');
  const [variableName, setVariableName] = useState('');
  const [fileDescription, setFileDescription] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const filteredFiles = fileTypeFilter 
    ? files.filter(f => f.fileType === fileTypeFilter)
    : files;

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', fileType);
      if (variableName) formData.append('variable_name', variableName);
      if (fileDescription) formData.append('description', fileDescription);

      const response = await fetch(`/api/v1/sequences/${sequenceId}/files/`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const uploadedFile = await response.json();
      onFileUploaded(uploadedFile);
      
      // Reset form
      setVariableName('');
      setFileDescription('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err: any) {
      setError(err.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId: string) => {
    try {
      const response = await fetch(`/api/v1/sequences/${sequenceId}/files/${fileId}/`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to delete file');
      }

      onFileDeleted(fileId);
    } catch (err: any) {
      setError(err.message || 'Failed to delete file');
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <AttachFile /> {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {description}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Upload Form */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3, alignItems: 'flex-start' }}>
        {!fileTypeFilter && (
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>File Type</InputLabel>
            <Select
              value={fileType}
              label="File Type"
              onChange={(e) => setFileType(e.target.value as any)}
            >
              <MenuItem value="context">📚 RAG Context Only</MenuItem>
              <MenuItem value="attachment">📎 Attachment Only</MenuItem>
              <MenuItem value="both">📚📎 Both (RAG + Attachment)</MenuItem>
            </Select>
          </FormControl>
        )}

        <Box>
          <TextField
            size="small"
            label="Variable Name"
            value={variableName}
            onChange={(e) => setVariableName(e.target.value)}
            placeholder="e.g., contextDocs"
            sx={{ minWidth: 180 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Store file content in this variable
          </Typography>
        </Box>

        <TextField
          size="small"
          label="Description"
          value={fileDescription}
          onChange={(e) => setFileDescription(e.target.value)}
          placeholder="What's this file for?"
          sx={{ minWidth: 200, flexGrow: 1 }}
        />

        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={handleFileSelect}
        />

        <Button
          variant="contained"
          startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <CloudUpload />}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          sx={{ height: 40 }}
        >
          {uploading ? 'Uploading...' : 'Upload File'}
        </Button>
      </Box>

      {/* File List */}
      {filteredFiles.length > 0 ? (
        <List dense>
          {filteredFiles.map((file) => (
            <ListItem
              key={file.id}
              sx={{
                bgcolor: 'background.default',
                borderRadius: 1,
                mb: 0.5,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ mr: 2, color: 'primary.main' }}>
                {getFileIcon(file.mimeType)}
              </Box>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {file.originalFilename}
                    </Typography>
                    <Chip
                      size="small"
                      label={file.fileType === 'both' ? 'RAG + Attachment' : file.fileType}
                      color={file.fileType === 'context' ? 'primary' : file.fileType === 'attachment' ? 'secondary' : 'success'}
                    />
                    {file.hasTextContent && (
                      <Chip size="small" label="Text Extracted" color="success" variant="outlined" />
                    )}
                  </Box>
                }
                secondary={
                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(file.fileSize)}
                    </Typography>
                    {file.variableName && (
                      <Typography variant="caption" color="primary.main">
                        Variable: {file.variableName}
                      </Typography>
                    )}
                    {file.description && (
                      <Typography variant="caption" color="text.secondary">
                        {file.description}
                      </Typography>
                    )}
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => handleDelete(file.id)}
                  sx={{ color: 'error.main' }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 3 }}>
          No files uploaded yet
        </Typography>
      )}
    </Paper>
  );
};

export default FileUploader;

